import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";

import TextInput from "../text-input";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};


const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        index: null,
        email: "",
      };
    }
    default:
      return state;
  }
};

const SingleProspectEmailsTable = (props) => {
  const { emails = [], handleChange, handleToggleEdit, disabled = false } = props;
  const [primaryEmail, ...otherEmails] = emails;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    index: null,
    email: "",
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddEmail = (e) => {
    e.preventDefault();

    handleToggleEdit("emails");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditEmail = (e, index) => {
    e.preventDefault();

    const email = emails[index];
    if (!email) {
      return;
    }

    handleToggleEdit("emails");
    dispatch({
      type: "openModal",
      payload: {
        email,
        index,
      },
    });
  };

  const handleDeleteEmail = (e, index) => {
    e.preventDefault();

    const email = emails[index];
    if (!email) {
      return;
    }

    handleToggleEdit("emails");
    dispatch({
      type: "openDeleteModal",
      payload: {
        email,
        index,
      },
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();

    if (!state.email) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange(
      state.index !== null ?
        (emails || []).map((item, index) => index === state.index ? state.email : item) :
        [...(emails || []), state.email],
    );

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });
    handleCloseModal();
  };

  const handleDeleteEmailConfirm = async (e) => {
    e.preventDefault();

    if (state.index === null) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((emails || []).filter((_, index) => index !== state.index));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <tr className="heading-row">
        <td colSpan={3}>Primary Email <span className="required">*</span></td>
        <td/>
      </tr>

      {primaryEmail && <tr>
        <td colSpan={3}>
          <div className="prospect-multi-rows-data">
            {primaryEmail}
          </div>
        </td>
        <td>
          <button
            className="base-button icon-button evo-blue"
            onClick={(e) => {
              handleEditEmail(e, 0);
            }}
            disabled={disabled}
          >
            <span className="material-icons">edit</span>
          </button>
        </td>
      </tr>}

      {otherEmails.length > 0 && otherEmails.map((email, index) => (
        <Fragment key={index}>
          <tr className="heading-row">
            <td colSpan={3}>Other Email</td>
            <td/>
          </tr>

          <tr>
            <td colSpan={3}>
              <div className="prospect-multi-rows-data">
                {email}
              </div>
            </td>
            <td>
              <button
                className="base-button icon-button evo-blue"
                onClick={(e) => {
                  handleEditEmail(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">edit</span>
              </button>

              <button
                className="base-button icon-button btn-danger ml-2"
                onClick={(e) => {
                  handleDeleteEmail(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">delete</span>
              </button>
            </td>
          </tr>
        </Fragment>
      ))}

      <tr>
        <td colSpan={3}>
          <button
            className="mt-20 mb-20 action-button base-button base-blue-button"
            onClick={handleAddEmail}
            disabled={disabled}
          >
            Add Email
          </button>
        </td>
        <td/>
      </tr>


      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{state.index !== null ? "Edit" : "Add"} Email</h4>
        </div>

        <div className="modal-body base-modal">
          <form onSubmit={handleUpdateEmail} className="w-100 base-form mt-20">
            <TextInput
              name="email"
              label="Email"
              required
              type="email"
              handleChange={handleInputChange}
              value={state.email}
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <button className="base-button evo-blue flex-half" type="submit" disabled={state.saving}>
                Submit
              </button>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Email</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            Are you sure you want to delete this email address from the prospect’s profile?<br/>
            This action cannot be undone.
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleDeleteEmailConfirm}
                    disabled={state.saving}>
              Delete Email
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectEmailsTable;
